import { SiteUrls } from '@tableau/shared-urls';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';
import * as _ from 'underscore';

import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { StyledCellFormatter } from '../../common/react/components/dataGrid/StyledCellWrapper';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { FlowOverviewErrorsCellComponent } from '../../flow/FlowDataGridDefinitions';
import { ConnectionDetailInfo, isRemote } from '../../ts/models/ConnectionDetailInfo';
import { FlowOutputStepRunListItemInfo } from '../../ts/models/FlowOutputStepRunInfo';
import { IDataConnectionInfo } from '../../ts/models/IDataConnectionInfo';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { FlowAllowedActions } from '../../ts/ServerApi/types/FlowAllowedActions';
import { FlowInputStep } from '../../ts/ServerApi/types/FlowInputStep';
import { FlowOutputStep } from '../../ts/ServerApi/types/FlowOutputStep';
import { FlowRunErrorUniqueKey } from '../../ts/ServerApi/types/FlowRunError';
import { IActionButton } from '../../ts/services/ActionPanelService';
import { ConnectsToCell } from './DataConnectionComponents';
import { DatasourceGrid } from './DatasourceGrid';
import { itemsCellWithPopover } from './ItemsCellWithPopover';

export module DataConnectionsGrid {

  export type DataConnectionsGridScope = CommonDataGridColumns.ISelectColumnScope<IDataConnectionInfo>;

  export function columns(
    translate: IReactTranslateService,
    scope: DataConnectionsGridScope,
    siteUrls: SiteUrls,
    actions: IActionButton<IDataConnectionInfo, {}>[],
    getActionTarget: () => IActionTarget<IDataConnectionInfo, {}>,
    anyActionsAllowed: boolean,
    inDatasourcePlace: boolean,
    inWorkbookPlace: boolean,
  ): DataGrid.Column[] {
    return _.compact([
      selectedColumn(anyActionsAllowed, scope),
      connectsToColumn(actions, anyActionsAllowed, getActionTarget, scope, siteUrls, translate),
      connectionTypeColumn(translate),
      authenticationColumn(translate),
      usernameColumn(translate),
      datasourceColumn(inDatasourcePlace, inWorkbookPlace, siteUrls, translate),
    ]);
  }

  export function columnsForFlow(
    actions: IActionButton<IDataConnectionInfo, {}>[],
    anyActionsAllowed: boolean,
    flowId: string,
    flowAllowedActions: FlowAllowedActions,
    getActionTarget: () => IActionTarget<IDataConnectionInfo, {}>,
    getFlowOutputStepRuns: () => (ReadonlyArray<FlowOutputStepRunListItemInfo> | null),
    getInputSteps: () => (ReadonlyArray<FlowInputStep> | null),
    getOutputSteps: () => (ReadonlyArray<FlowOutputStep> | null),
    scope: DataConnectionsGridScope,
    siteUrls: SiteUrls,
    translate: IReactTranslateService,
  ): DataGrid.Column[] {
    return _.compact([
      selectedColumn(anyActionsAllowed, scope),
      connectsToColumn(actions, anyActionsAllowed, getActionTarget, scope, siteUrls, translate),
      connectionTypeColumn(translate),
      authenticationColumn(translate),
      usernameColumn(translate),
      flowInputStepsColumn(getInputSteps, translate),
      flowOutputStepsColumn(getOutputSteps, translate),
      flowConnectionErrorColumn(flowId, getFlowOutputStepRuns, translate, siteUrls, flowAllowedActions.download),
    ]);
  }

  function selectedColumn(
    anyActionsAllowed: boolean,
    scope: DataConnectionsGridScope,
  ): DataGrid.Column | null {
    return anyActionsAllowed ? CommonDataGridColumns.selected(scope) : null;
  }

  function connectsToColumn(
    actions: IActionButton<IDataConnectionInfo, {}>[],
    anyActionsAllowed: boolean,
    getActionTarget: () => IActionTarget<IDataConnectionInfo, {}>,
    scope: DataConnectionsGridScope,
    siteUrls: SiteUrls,
    translate: IReactTranslateService,
  ): DataGrid.Column {
    const column = CommonDataGridColumns.withExtraSpace({
      name: 'connects-to-col',
      dataKey: 'connectsTo',
      headerText: translate('column_connects_to'),
      sortable: false,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: ConnectsToCell.cellFormatter(siteUrls, translate)
    });
    if (anyActionsAllowed) {
      return CommonDataGridColumns.withActions(column, {
        actions: actions,
        getActionTarget: getActionTarget,
        translate: translate
      }, scope);
    } else {
      return column;
    }
  }

  function connectionTypeColumn(
    translate: IReactTranslateService,
  ): DataGrid.Column {
    return {
      name: 'connection-type-col',
      dataKey: 'connectionTypeDisplayName',
      sortable: true,
      headerText: translate('column_connection_type'),
      headerFormatter: SortableHeader.Formatter(),
      direction: 'asc'
    };
  }

  function authenticationColumn(
    translate: IReactTranslateService,
  ): DataGrid.Column {
    return {
      name: 'authentication-col',
      dataKey: 'authentication',
      sortable: false,
      headerText: translate('authentication'),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: AuthenticationCellFormatter(translate)
    };
  }

  function usernameColumn(
    translate: IReactTranslateService,
  ): DataGrid.Column {
    return {
      name: 'username-col',
      dataKey: 'username',
      sortable: false,
      headerText: translate('username'),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: UsernameCellFormatter()
    };
  }

  function datasourceColumn(
    inDatasourcePlace: boolean,
    inWorkbookPlace: boolean,
    siteUrls: SiteUrls,
    translate: IReactTranslateService,
  ): DataGrid.Column | null {
    if (inDatasourcePlace) {
      return null;
    } else {
      return {
        name: 'datasource-col',
        dataKey: 'name', // this data key is used by sorting
        sortable: true,
        headerText: translate('datasource'),
        direction: 'asc',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataConnectionContainerCellFormatter(siteUrls, translate, inWorkbookPlace)
      };
    }
  }

  function flowInputStepsColumn(
    getInputSteps: () => (ReadonlyArray<FlowInputStep> | null),
    translate: IReactTranslateService,
  ): DataGrid.Column {
    return {
      name: 'input-steps-col',
      dataKey: 'flowInputSteps',
      headerText: translate('inputSteps'),
      sortable: false,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: itemsCellWithPopover(makeGetStepNamesForRow(getInputSteps), 'inputStepsCount', translate)
    };
  }

  function flowOutputStepsColumn(
    getOutputSteps: () => (ReadonlyArray<FlowOutputStep> | null),
    translate: IReactTranslateService,
  ): DataGrid.Column {
    return {
      name: 'output-steps-col',
      dataKey: 'flowOutputSteps',
      headerText: translate('outputSteps'),
      sortable: false,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: itemsCellWithPopover(makeGetStepNamesForRow(getOutputSteps), 'outputStepsCount', translate)
    };
  }

  function flowConnectionErrorColumn(
    flowId: string,
    getMostRecent: () => (ReadonlyArray<FlowOutputStepRunListItemInfo> | null),
    translate: IReactTranslateService,
    siteUrls: SiteUrls,
    isDownloadAllowed: boolean
  ): DataGrid.Column {
    return {
      name: 'errors-col',
      dataKey: 'errors',
      headerText: translate('flowRunHistoryColumn_errors'),
      sortable: false,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: ConnectionErrorCell(flowId, getMostRecent, siteUrls, isDownloadAllowed)
    };
  }

  function ConnectionErrorCell(
    flowId: string,
    getMostRecentRuns: () => (ReadonlyArray<FlowOutputStepRunListItemInfo> | null),
    siteUrls: SiteUrls,
    isDownloadAllowed: boolean,
  ): React.StatelessComponent<BodyCell.Props> {
    return function ConnectionErrorCell(props: BodyCell.Props) {
      const connection = props.row.data as IDataConnectionInfo;
      const mostRecentRuns = getMostRecentRuns();

      const uniqueConnectionErrors = mostRecentRuns == null ? [] : _
        .chain(getMostRecentRuns().map(run => run.flowRunErrors))
        .flatten()
        .filter(error => error.errorObjectId === connection.id)
        .uniq(false, FlowRunErrorUniqueKey)
        .value();

      return (
        <FlowOverviewErrorsCellComponent
          errors={uniqueConnectionErrors}
          flowId={flowId}
          onConnectionsPage={true}
          siteUrls={siteUrls}
          isDownloadAllowed
        />
      );
    };
  }

  function makeGetStepNamesForRow(getSteps: () => (ReadonlyArray<FlowInputStep | FlowOutputStep> | null)) {
    return function getStepNamesForRow(row: DataGrid.Row): ReadonlyArray<INamedItemInfo> {
      const connection = row.data as IDataConnectionInfo;
      const steps = getSteps();
      if (connection && steps) {
        return _.filter(steps, (step) => step.connectionId === connection.id);
      }
      return [];
    };
  }

  export function AuthenticationCellFormatter(
    translate: IReactTranslateService
  ): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      (props: BodyCell.Props): React.ReactElement<any> => {
        const connection = props.row.data as IDataConnectionInfo;
        const details: ConnectionDetailInfo = connection.connectionDetails;

        let text = '';
        if (isRemote(details)) {
          if (details.hasEmbeddedPassword) {
            text = translate('hasPasswordEmbedded');
          } else {
            text = translate('hasPasswordNotEmbedded');
          }
        } else if (details.type === 'file') {
          text = translate('none');
        }

        // Tableau server connections return empty string
        return React.createElement('span', {}, text);
      }
    );
  }

  export function UsernameCellFormatter(): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      (props: BodyCell.Props): React.ReactElement<any> => {
        const connection = props.row.data as IDataConnectionInfo;
        const details: ConnectionDetailInfo = connection.connectionDetails;

        let text = '';
        if (isRemote(details)) {
          text = details.databaseUsername;
        }

        // File, Tableau Server, and Web Data connections return empty string
        return React.createElement('span', {}, text);
      }
    );
  }

  // A data connection can be in either a data source or a flow.
  // This component renders the appropriate information in either case.
  // However, since there is currently no place where we list flow connections alongside
  // other connections, we never display flow container information for flows.
  // Thus, the flow path is not currently exercised.
  // How we expose flow connections alongside other connections is still under discussion.
  // Once we understand that, I expect we'll fill in the flow path.
  export function DataConnectionContainerCellFormatter(
    siteUrls: SiteUrls,
    translate: IReactTranslateService,
    inWorkbookPlace: boolean
  ): React.StatelessComponent<BodyCell.Props> {
    return (props: BodyCell.Props): React.ReactElement<any> | null => {
      const connection = props.row.data as IDataConnectionInfo;
      const datasource = connection.datasource;
      if (datasource) {
        const formatter = DatasourceGrid.DatasourceNameCellFormatter(
          (data: IDataConnectionInfo) => datasource, siteUrls, translate, inWorkbookPlace);
        return formatter(props);
      } else {
        const flow = connection.flow;
        if (flow) {
          return null; // TODO: Implement something, once we're using this path.
        }
      }
      return null;
    };
  }

}
